import type { UserRole, UserStatus } from "./auth";
import { apiRequest, apiResponse } from "./client";

export type AuditResult = "SUCCEEDED" | "FAILED" | "DENIED";
export type AuditCategory = "LOGIN" | "PROJECT" | "ATTACHMENT" | "USER";
export type AuditEventKind = "BUSINESS" | "ACCESS" | "SECURITY" | "SYSTEM";
export type AuditActorKind = "USER" | "SYSTEM" | "ANONYMOUS";
export type AuditFileType = "IMAGE" | "PDF" | "DOCUMENT" | "VIDEO" | "AUDIO" | "OTHER";

export interface AuditSubject {
  project_id: string | null;
  project_number: string | null;
  work_name: string | null;
  attachment_name: string | null;
  attachment_size_bytes: number | null;
  attachment_content_type: string | null;
  target_user_name: string | null;
  target_user_phone_masked: string | null;
}

export interface AuditEvent {
  id: string;
  event_type: string;
  result: AuditResult;
  actor_username: string | null;
  actor_name: string | null;
  actor_role: UserRole | null;
  actor_kind: AuditActorKind;
  event_kind: AuditEventKind;
  target_type: string | null;
  target_id: string | null;
  source_ip: string;
  user_agent: string;
  request_id: string;
  reason: string | null;
  payload: Record<string, unknown> | null;
  subject: AuditSubject;
  created_at: string;
}

export interface AuditListResponse {
  items: AuditEvent[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditSummary {
  today_login_accounts: number;
  today_projects_created: number;
  today_projects_deleted: number;
  today_project_changes: number;
  today_unique_attachment_previews: number;
  today_unique_attachment_downloads: number;
  today_unique_attachment_accesses: number;
  all_risk_events: number;
  category_counts: Record<AuditCategory, number>;
}

export interface AuditEventFilters {
  page: number;
  pageSize: number;
  category?: AuditCategory;
  eventType?: string;
  result?: AuditResult;
  targetId?: string;
  actorKeyword?: string;
  actorRole?: UserRole;
  keyword?: string;
  projectKeyword?: string;
  attachmentKeyword?: string;
  targetUserKeyword?: string;
  fileType?: AuditFileType;
  targetRole?: UserRole;
  targetStatus?: UserStatus;
  sourceIp?: string;
  requestId?: string;
  createdFrom?: string;
  createdTo?: string;
  includeSystem?: boolean;
  includeTestData?: boolean;
}

export function fetchAuditEvents(
  filters: AuditEventFilters,
  signal?: AbortSignal,
): Promise<AuditListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
  });
  if (filters.category) params.set("category", filters.category);
  if (filters.eventType) params.set("event_type", filters.eventType);
  if (filters.result) params.set("result", filters.result);
  if (filters.targetId) params.set("target_id", filters.targetId);
  if (filters.actorKeyword) params.set("actor_keyword", filters.actorKeyword);
  if (filters.actorRole) params.set("actor_role", filters.actorRole);
  if (filters.keyword) params.set("keyword", filters.keyword);
  if (filters.projectKeyword) params.set("project_keyword", filters.projectKeyword);
  if (filters.attachmentKeyword) params.set("attachment_keyword", filters.attachmentKeyword);
  if (filters.targetUserKeyword) params.set("target_user_keyword", filters.targetUserKeyword);
  if (filters.fileType) params.set("file_type", filters.fileType);
  if (filters.targetRole) params.set("target_role", filters.targetRole);
  if (filters.targetStatus) params.set("target_status", filters.targetStatus);
  if (filters.sourceIp) params.set("source_ip", filters.sourceIp);
  if (filters.requestId) params.set("request_id", filters.requestId);
  if (filters.createdFrom) params.set("created_from", filters.createdFrom);
  if (filters.createdTo) params.set("created_to", filters.createdTo);
  if (filters.includeSystem !== undefined) {
    params.set("include_system", String(filters.includeSystem));
  }
  if (filters.includeTestData !== undefined) {
    params.set("include_test_data", String(filters.includeTestData));
  }
  return apiRequest(`/api/v1/admin/audit-events?${params}`, { signal });
}

export function fetchAuditSummary(signal?: AbortSignal): Promise<AuditSummary> {
  return apiRequest("/api/v1/admin/audit-events/summary", { signal });
}

export interface AuditExportJob {
  id: string;
  filters: Record<string, unknown>;
  status: "PENDING" | "PROCESSING" | "READY" | "FAILED";
  sha256: string | null;
  size_bytes: number | null;
  event_count: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export function createAuditExport(filters: Record<string, unknown>): Promise<AuditExportJob> {
  return apiRequest("/api/v1/admin/audit-exports", {
    method: "POST",
    body: JSON.stringify(filters),
  });
}

export function fetchAuditExports(signal?: AbortSignal): Promise<{ items: AuditExportJob[] }> {
  return apiRequest("/api/v1/admin/audit-exports", { signal });
}

export async function downloadAuditExport(jobId: string): Promise<Blob> {
  const ticket = await apiRequest<{ download_url: string }>(
    `/api/v1/admin/audit-exports/${jobId}/download-ticket`,
    { method: "POST" },
  );
  const response = await apiResponse(ticket.download_url, {
    headers: { Accept: "application/zip" },
  });
  return response.blob();
}
